import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bike,
  CalendarClock,
  ChevronRight,
  ExternalLink,
  MessageSquare,
  Package,
  Printer,
  Search,
  Store,
} from 'lucide-react';
import {
  DUE_BUCKET_LABELS,
  formatMoney,
  formatShopDate,
  formatShopTime,
  relativeTime,
  type DueBucket,
  type OrderStatus,
  type OrderWithDetail,
} from '@bakery/shared';

import { api, type NotifyResult } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { printReceipt } from '../lib/printing.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

const BUCKETS: DueBucket[] = ['overdue', 'today', 'tomorrow', 'advance', 'undated'];

/**
 * The tabs that ask about the order rather than its due date.
 *
 * Between them these four account for every order exactly once: owed for,
 * paid and still to hand over, finished, or called off.
 */
const VIEWS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

type OrderView = (typeof VIEWS)[number]['value'];

const VIEW_EMPTY: Record<OrderView, string> = {
  unpaid: 'Nothing is owed. Every order has been paid for in full.',
  paid: 'Nothing is paid and waiting. Orders appear here once they are settled but not yet handed over.',
  completed: 'Orders that have been dispatched or collected will appear here.',
  cancelled: 'No orders have been called off.',
};

const FULFILLMENT_ICON = {
  pickup: Store,
  delivery: Bike,
  dine_in: Store,
  courier: Package,
} as const;

export function OrdersScreen() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [bucket, setBucket] = useState<DueBucket | 'all' | OrderView>('today');
  const [search, setSearch] = useState('');

  /*
   * Completed orders only accumulate — a month of imported sales already runs
   * to a few hundred — so this page asks for a window of them and grows it on
   * request. Without it the list silently stops at the server's default and a
   * shop looking for an older sale is told, wrongly, that there is nothing
   * more.
   */
  const PAGE = 100;
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [bucket, search]);

  const isView = VIEWS.some((view) => view.value === bucket);

  const statuses = useQuery({ queryKey: ['order-statuses'], queryFn: () => api.orders.statuses() });
  const buckets = useQuery({ queryKey: ['orders', 'buckets'], queryFn: api.orders.buckets, refetchInterval: 60_000 });
  const orders = useQuery({
    queryKey: ['orders', bucket, search, limit],
    queryFn: () =>
      api.orders.list({
        bucket: isView || bucket === 'all' ? undefined : bucket,
        view: isView ? bucket : undefined,
        // Any of these can run to hundreds, so they page rather than stopping
        // silently at the server's default.
        limit: isView ? limit : undefined,
        /*
         * A search looks for one order, and the one you are hunting is usually
         * the one already dealt with — yesterday's sale, last week's box of
         * croissants. Restricting it to open work means the answer is "no
         * orders here" for everything the shop has actually finished.
         */
        includeTerminal: search ? true : undefined,
        search: search || undefined,
      }),
    refetchInterval: 60_000,
  });

  const countFor = (value: DueBucket) => buckets.data?.find((b) => b.bucket === value)?.count ?? 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold">Orders</h1>
        <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
          Grouped by when the customer needs them, not by when they were placed.
        </p>
      </header>

      <div className="mb-4 space-y-3">
        <Tabs
          value={bucket}
          onChange={setBucket}
          tabs={[
            ...BUCKETS.filter((value) => value !== 'undated' || countFor('undated') > 0).map((value) => ({
              value,
              label: DUE_BUCKET_LABELS[value],
              count: countFor(value),
              tone: value === 'overdue' ? ('danger' as const) : undefined,
            })),
            { value: 'all' as const, label: 'All open' },
            ...VIEWS.map((view) => ({ value: view.value, label: view.label })),
          ]}
        />

        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Order number, customer name or phone…"
            className="pl-9"
          />
        </div>
      </div>

      {orders.isError && <ErrorNote error={orders.error} retry={() => orders.refetch()} />}

      {orders.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : orders.data?.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="size-8" />}
          title={
            search ? 'Nothing matches that' : bucket === 'today' ? 'Nothing due today' : 'No orders here'
          }
          description={
            search
              ? 'Searching covers finished orders as well as open ones, so try a different number or name.'
              : isView
                ? VIEW_EMPTY[bucket as OrderView]
                : bucket === 'today'
                  ? 'Orders due today will appear here as they come in from the register or the inbox.'
                  : 'Try another tab, or clear the search.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {orders.data?.map((order) => (
            <OrderRow key={order.id} order={order} onOpen={() => navigate(`/orders/${order.id}`)} />
          ))}

          {isView && orders.data?.length === limit && (
            <li className="pt-2 text-center">
              <Button variant="ghost" onClick={() => setLimit((current) => current + PAGE)}>
                Show older orders
              </Button>
            </li>
          )}
        </ul>
      )}

      {orderId && (
        <OrderDetail
          orderId={orderId}
          statuses={statuses.data ?? []}
          onClose={() => navigate('/orders')}
        />
      )}
    </div>
  );
}

function OrderRow({ order, onOpen }: { order: OrderWithDetail; onOpen: () => void }) {
  const Icon = FULFILLMENT_ICON[order.fulfillment] ?? Store;
  const isLate = order.dueBucket === 'overdue';

  return (
    <li>
      <button
        onClick={onOpen}
        className={cx(
          'card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400 hover:elevation-raised',
          isLate && 'border-[var(--color-danger)]/40',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm font-semibold">{order.orderNo}</span>
            <Badge color={order.status.color}>{order.status.name}</Badge>
            {isLate && (
              <Badge tone="danger">
                <AlertTriangle className="size-3" /> Late
              </Badge>
            )}
            {order.balanceDue > 0 && <Badge tone="warn">{formatMoney(order.balanceDue)} owed</Badge>}
          </div>

          <p className="mt-1 truncate text-sm text-[var(--color-ink)]">
            {order.customerDisplayName ?? 'Walk-in customer'}
          </p>

          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--color-ink-soft)]">
            <span className="inline-flex items-center gap-1">
              <Icon className="size-3.5" />
              {order.fulfillment === 'delivery' ? 'Delivery' : 'Pickup'}
            </span>
            {/*
              * A due date if it has one, otherwise when it was finished.
              *
              * Most of the imported sales carry no due date — they were rung
              * up and handed over at the counter — so a finished list showed
              * no date at all against them, which made a list sorted by date
              * impossible to read. "Done" rather than a bare date, so it is
              * never mistaken for something still expected.
              */}
            {order.dueAt ? (
              <span>
                {formatShopDate(order.dueAt)} · {formatShopTime(order.dueAt)}
              </span>
            ) : order.completedAt ? (
              <span>
                Done {formatShopDate(order.completedAt)} · {formatShopTime(order.completedAt)}
              </span>
            ) : null}
            <span className="truncate">
              {order.lines
                .slice(0, 2)
                .map((line) => `${line.qty}× ${line.nameSnapshot}`)
                .join(', ')}
              {order.lines.length > 2 && ` +${order.lines.length - 2} more`}
            </span>
          </p>
        </div>

        <span className="tabular shrink-0 font-display text-lg font-semibold">{formatMoney(order.total)}</span>
      </button>
    </li>
  );
}

function OrderDetail({
  orderId,
  statuses,
  onClose,
}: {
  orderId: string;
  statuses: OrderStatus[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [takingPayment, setTakingPayment] = useState(false);

  const order = useQuery({ queryKey: ['order', orderId], queryFn: () => api.orders.get(orderId) });

  const print = useMutation({
    mutationFn: () => api.orders.markPrinted(orderId),
    onSuccess: async (receipt) => {
      await printReceipt(receipt);
      toast.push({ tone: 'ok', title: `Receipt ${receipt.receiptNo} sent to the printer` });
    },
  });

  function announce(result: NotifyResult | null) {
    if (!result) return;
    if (result.status === 'sent') {
      toast.push({ tone: 'ok', title: `Customer notified on ${result.channel}` });
    } else if (result.handoffUrl) {
      // The channel is not connected, but the message is written and ready.
      toast.push({
        tone: 'warn',
        title: 'Message ready to send',
        body: result.error ?? 'This channel needs finishing in its own app.',
        action: { label: `Open ${result.channel ?? 'app'} to send`, href: result.handoffUrl },
      });
    } else if (result.status === 'skipped') {
      toast.push({ tone: 'neutral', title: 'No message sent', body: result.error ?? undefined });
    } else {
      toast.push({ tone: 'danger', title: 'Message failed', body: result.error ?? undefined });
    }
  }

  if (order.isLoading) {
    return (
      <Modal open onClose={onClose} title="Loading order…">
        <Skeleton className="h-64" />
      </Modal>
    );
  }

  if (!order.data) {
    return (
      <Modal open onClose={onClose} title="Order not found">
        <p className="text-sm text-[var(--color-ink-soft)]">That order no longer exists.</p>
      </Modal>
    );
  }

  const data = order.data;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`${data.orderNo} — ${data.customerDisplayName ?? 'Walk-in'}`}
        description={
          data.dueAt
            ? `${data.fulfillment === 'delivery' ? 'Deliver' : 'Ready'} ${formatShopDate(data.dueAt)} at ${formatShopTime(data.dueAt)}`
            : 'No scheduled time'
        }
        footer={
          <>
            {/* Voiding a sale is its own permission, and not every till has it. */}
            {can('pos.void') && (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
            <Button variant="ghost" icon={<Printer className="size-4" />} loading={print.isPending} onClick={() => print.mutate()}>
              Receipt
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/*
            * Back to the conversation this order came from.
            *
            * The other half of the link the Inbox shows: an order raised from
            * a chat had no way back to it, so answering "what did they
            * actually ask for?" meant searching the Inbox by name.
            */}
          {data.conversationId && (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/inbox/${data.conversationId}`);
              }}
              className="flex w-full items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-info-soft)] px-3 py-2 text-left text-sm transition hover:brightness-95"
            >
              <MessageSquare className="size-4 shrink-0 text-[var(--color-info)]" />
              <span className="min-w-0 flex-1 truncate">
                From the chat with <strong>{data.customerDisplayName ?? 'this customer'}</strong>
              </span>
              <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--color-info)]">
                Open chat <ChevronRight className="size-3.5" />
              </span>
            </button>
          )}

          {/* Status pipeline ------------------------------------------- */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Move to</h3>
            <div className="flex flex-wrap gap-2">
              {statuses
                .filter((status) => status.id !== data.statusId)
                .map((status) => (
                  <button
                    key={status.id}
                    onClick={() => setPendingStatus(status)}
                    style={{ borderColor: `${status.color}66`, color: status.color }}
                    className="rounded-[var(--radius-md)] border-2 px-3 py-2 text-sm font-medium transition hover:bg-[var(--color-surface-sunken)]"
                  >
                    {status.name}
                  </button>
                ))}
            </div>
          </section>

          {/* Lines ------------------------------------------------------ */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Items</h3>
            <Card className="divide-y divide-[var(--color-line)] overflow-hidden">
              {data.lines.map((line) => (
                <div key={line.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="tabular w-8 text-sm font-medium">{line.qty}×</span>
                  <span className="min-w-0 flex-1 truncate text-sm">{line.nameSnapshot}</span>
                  <span className="tabular text-sm">{formatMoney(line.lineTotal)}</span>
                </div>
              ))}
              <div className="space-y-1 bg-[var(--color-surface-sunken)] px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-ink-soft)]">Subtotal</span>
                  <span className="tabular">{formatMoney(data.subtotal)}</span>
                </div>
                {data.discountAmount > 0 && (
                  <div className="flex justify-between text-[var(--color-ok)]">
                    <span>Discount</span>
                    <span className="tabular">-{formatMoney(data.discountAmount)}</span>
                  </div>
                )}
                {data.deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--color-ink-soft)]">Delivery</span>
                    <span className="tabular">{formatMoney(data.deliveryFee)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-[var(--color-line)] pt-1.5 font-semibold">
                  <span>Total</span>
                  <span className="tabular">{formatMoney(data.total)}</span>
                </div>
                {data.amountPaid > 0 && (
                  <div className="flex justify-between text-[var(--color-ink-soft)]">
                    <span>Paid</span>
                    <span className="tabular">-{formatMoney(data.amountPaid)}</span>
                  </div>
                )}
                {data.balanceDue > 0 && (
                  <div className="flex justify-between font-semibold text-[var(--color-warn)]">
                    <span>Balance due</span>
                    <span className="tabular">{formatMoney(data.balanceDue)}</span>
                  </div>
                )}
              </div>

              {/*
                * Moving an order to "Paid" is a position in the workflow, not a
                * receipt of money — nothing but a payment clears the balance.
                * Until this button existed, an order raised from a conversation
                * could never be settled at all: only the register recorded
                * payments, and it does so at checkout.
                */}
              {data.balanceDue > 0 && can('pos.sell') && (
                <Button
                  variant="primary"
                  className="mt-3 w-full"
                  onClick={() => setTakingPayment(true)}
                >
                  Record payment of {formatMoney(data.balanceDue)}
                </Button>
              )}
            </Card>
          </section>

          {(data.addressSnapshot || data.productionNotes || data.notes) && (
            <section className="space-y-2 text-sm">
              {data.addressSnapshot && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">Address: </span>
                  {data.addressSnapshot}
                </p>
              )}
              {data.productionNotes && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">For the baker: </span>
                  {data.productionNotes}
                </p>
              )}
              {data.notes && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">Notes: </span>
                  {data.notes}
                </p>
              )}
            </section>
          )}

          {/* Timeline --------------------------------------------------- */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">History</h3>
            <ol className="space-y-2">
              {data.events.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-crust-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[var(--color-ink)]">{event.note}</p>
                    <p className="text-xs text-[var(--color-ink-faint)]">
                      {relativeTime(event.createdAt)}
                      {event.notificationStatus && ` · message ${event.notificationStatus}`}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </Modal>

      {pendingStatus && (
        <StatusChangeModal
          order={data}
          status={pendingStatus}
          onClose={() => setPendingStatus(null)}
          onDone={(result) => {
            announce(result);
            setPendingStatus(null);
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
          }}
        />
      )}

      {takingPayment && (
        <TakePaymentModal
          order={data}
          statuses={statuses}
          onClose={() => setTakingPayment(false)}
          onDone={(result) => {
            announce(result);
            setTakingPayment(false);
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
          }}
        />
      )}

      {confirmDelete && (
        <DeleteOrderModal
          order={data}
          onCancel={() => setConfirmDelete(false)}
          onDeleted={() => {
            setConfirmDelete(false);
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            onClose();
          }}
        />
      )}
    </>
  );
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'gcash', label: 'GCash' },
  { value: 'maya', label: 'Maya' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' },
] as const;

function TakePaymentModal({
  order,
  statuses,
  onClose,
  onDone,
}: {
  order: OrderWithDetail;
  statuses: OrderStatus[];
  onClose: () => void;
  onDone: (result: NotifyResult | null) => void;
}) {
  const toast = useToast();
  const [method, setMethod] = useState<string>('cash');
  const [amount, setAmount] = useState(String(order.balanceDue / 100));
  const [reference, setReference] = useState('');
  const [tendered, setTendered] = useState('');

  const centavos = Math.round(Number(amount.replace(/[^\d.]/g, '')) * 100);
  const tenderedCentavos = Math.round(Number(tendered.replace(/[^\d.]/g, '')) * 100);
  const valid = Number.isFinite(centavos) && centavos > 0;
  const change = method === 'cash' && tenderedCentavos > centavos ? tenderedCentavos - centavos : 0;

  /*
   * A payment only speaks to the customer when it settles the order and moves
   * it to Paid. A part payment, or money caught up on an order already handed
   * over, changes no status and so says nothing — and the dialog should not
   * offer to send a message that cannot go.
   */
  const paidStatus = statuses.find((s) => s.systemKey === 'paid');
  const willSettle = !order.status.countsAsPaid && valid && centavos >= order.balanceDue;

  const [notify, setNotify] = useState(paidStatus?.autoNotify ?? false);
  const [messageBody, setMessageBody] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['order-preview', order.id, paidStatus?.id],
    queryFn: () => api.orders.previewMessage(order.id, paidStatus!.id),
    enabled: Boolean(paidStatus) && willSettle,
  });

  const text = messageBody ?? preview.data?.preview ?? '';
  const canSend = willSettle && Boolean(preview.data?.hasTemplate);

  const pay = useMutation({
    mutationFn: () =>
      api.orders.addPayment(order.id, {
        method,
        amount: centavos,
        reference: reference || null,
        tendered: method === 'cash' && tenderedCentavos > 0 ? tenderedCentavos : undefined,
        /*
         * Settling normally advances the order to Paid. These are often
         * payments caught up after the fact, on an order already handed over,
         * and dragging it back to Paid would undo real progress. The server
         * already refuses to advance a status that counts as paid; this is the
         * belt to that braces.
         */
        autoAdvance: !order.status.countsAsPaid,
        notify: canSend ? notify : false,
        bodyOverride: canSend && notify && messageBody ? messageBody : undefined,
      }),
    onSuccess: ({ notification }) => {
      toast.push({ tone: 'ok', title: `${formatMoney(centavos)} recorded` });
      onDone(notification);
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not record it', body: error.message }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Payment for ${order.orderNo}`}
      description={`${formatMoney(order.balanceDue)} outstanding of ${formatMoney(order.total)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={pay.isPending} disabled={!valid} onClick={() => pay.mutate()}>
            {canSend && notify ? 'Record and send' : 'Record payment'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="How did they pay?">
          <Select value={method} onChange={(event) => setMethod(event.target.value)}>
            {PAYMENT_METHODS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Amount" hint="Less than the balance records a part payment.">
          <Input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" autoFocus />
        </Field>

        {method === 'cash' ? (
          <Field label="Cash given" hint={change > 0 ? `Change due ${formatMoney(change)}` : 'Optional.'}>
            <Input value={tendered} onChange={(event) => setTendered(event.target.value)} inputMode="decimal" />
          </Field>
        ) : (
          <Field label="Reference" hint="The GCash or transfer reference, if there is one.">
            <Input value={reference} onChange={(event) => setReference(event.target.value)} />
          </Field>
        )}

        {valid && centavos < order.balanceDue && (
          <p className="text-sm text-[var(--color-ink-soft)]">
            {formatMoney(order.balanceDue - centavos)} will still be owed after this, so the order stays where it is
            and nothing is sent to the customer.
          </p>
        )}

        {canSend && (
          <div className="space-y-4 border-t border-[var(--color-line)] pt-4">
            <label className="flex items-center gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={notify}
                onChange={(event) => setNotify(event.target.checked)}
                className="size-4 accent-[var(--color-crust-600)]"
              />
              <span className="font-medium">Send this message to {order.customerDisplayName ?? 'the customer'}</span>
            </label>

            {notify && (
              <Field label="Message" hint="Edit it if this order needs something different. The saved template is unchanged.">
                <Textarea rows={6} value={text} onChange={(event) => setMessageBody(event.target.value)} />
              </Field>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function DeleteOrderModal({
  order,
  onCancel,
  onDeleted,
}: {
  order: OrderWithDetail;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const toast = useToast();
  const [confirmation, setConfirmation] = useState('');

  const remove = useMutation({
    mutationFn: () => api.orders.remove(order.id),
    onSuccess: () => {
      toast.push({ tone: 'ok', title: `${order.orderNo} deleted` });
      onDeleted();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not delete', body: error.message }),
  });

  const confirmed = confirmation.trim().toLowerCase() === order.orderNo.trim().toLowerCase();
  const paid = order.payments?.length ?? 0;

  return (
    <Modal
      open
      onClose={onCancel}
      title={`Delete ${order.orderNo}?`}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="danger" loading={remove.isPending} disabled={!confirmed} onClick={() => remove.mutate()}>
            Delete for good
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/*
          * Cancelling and deleting are not the same thing, and the difference
          * is the whole point of this screen. One leaves a record of a sale
          * that did not happen; the other leaves nothing at all.
          */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface-sunken)] p-3 text-sm">
          <p className="font-medium">Did you mean to cancel it instead?</p>
          <p className="mt-1 text-[var(--color-ink-soft)]">
            Cancelling returns the ingredients and keeps the order in the books, so the day still adds up and you can
            find it later. Deleting leaves nothing behind.
          </p>
        </div>

        <ul className="space-y-1.5 text-sm">
          <li>
            The <strong>{formatMoney(order.total)}</strong> and its {order.lines.length} line
            {order.lines.length === 1 ? '' : 's'} disappear from every total and report.
          </li>
          {paid > 0 && (
            <li>
              <strong>
                {paid} payment{paid === 1 ? '' : 's'}
              </strong>{' '}
              recorded against it, and any receipt, go too.
            </li>
          )}
          <li>The timeline — who changed what, and when — is destroyed with it.</li>
          <li>Ingredients used for it go back into stock.</li>
        </ul>

        <Field label={`Type “${order.orderNo}” to confirm`}>
          <Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Changing status is also the moment the customer hears from us, so the message
 * is shown — and editable — before it goes, rather than after.
 */
function StatusChangeModal({
  order,
  status,
  onClose,
  onDone,
}: {
  order: OrderWithDetail;
  status: OrderStatus;
  onClose: () => void;
  onDone: (result: NotifyResult | null) => void;
}) {
  const [notify, setNotify] = useState(status.autoNotify);
  const [body, setBody] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['order-preview', order.id, status.id],
    queryFn: () => api.orders.previewMessage(order.id, status.id),
  });

  const move = useMutation({
    mutationFn: () =>
      api.orders.changeStatus(order.id, {
        statusId: status.id,
        notify,
        bodyOverride: body ?? undefined,
      }),
    onSuccess: (result) => onDone(result.notification),
  });

  const text = body ?? preview.data?.preview ?? '';

  return (
    <Modal
      open
      onClose={onClose}
      title={`Move to ${status.name}`}
      description={status.description ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={move.isPending} onClick={() => move.mutate()}>
            {notify && preview.data?.hasTemplate ? 'Move and send' : 'Move'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {preview.data?.hasTemplate ? (
          <>
            <label className="flex items-center gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={notify}
                onChange={(event) => setNotify(event.target.checked)}
                className="size-4 accent-[var(--color-crust-600)]"
              />
              <span className="font-medium">Send this message to {order.customerDisplayName ?? 'the customer'}</span>
            </label>

            {notify && (
              <Field label="Message" hint="Edit it if this order needs something different. The saved template is unchanged.">
                <Textarea
                  rows={10}
                  value={text}
                  onChange={(event) => setBody(event.target.value)}
                  className="font-mono text-[13px]"
                />
              </Field>
            )}
          </>
        ) : (
          <p className="text-sm text-[var(--color-ink-soft)]">
            No customer message is attached to “{status.name}”. You can add one in Settings → Order statuses.
          </p>
        )}
      </div>
    </Modal>
  );
}
